'use client';

import { useState } from 'react';
import { Send, Mail, Phone, MapPin, CircleCheck as CheckCircle } from 'lucide-react';
import { Heading } from '@/components/ui/typography';

const services = [
  'Brand Identity',
  'UI/UX Design',
  'Web Development',
  'Motion Design',
  'Growth Strategy',
  'Digital Marketing',
  'Other',
];

const budgets = [
  'Under $5,000',
  '$5,000 – $15,000',
  '$15,000 – $50,000',
  '$50,000+',
];

export default function ContactForm({ heading = "Get in Touch", successHeading = "Message Sent!" }: { heading?: string; successHeading?: string } = {}) {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    budget: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section className="bg-[#080d1c] py-16 pb-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <Heading as="h2" variant="cardTitleLight" className="mb-4">{heading}</Heading>
              <p className="font-suisse-intl text-gray-400 text-sm leading-relaxed">
                Whether you are ready to kick off a project or just want to explore possibilities, we are here to help. Let&apos;s talk.
              </p>
            </div>

            <div className="space-y-5">
              {[
                { icon: Mail, label: 'Email', value: 'hello@luminastudio.com' },
                { icon: Phone, label: 'Phone', value: '+1 (555) 234-5678' },
                { icon: MapPin, label: 'Office', value: '123 Design District\nSan Francisco, CA 94103' },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-start gap-4 p-5 rounded-xl bg-white/5 border border-white/10">
                  <div className="w-10 h-10 rounded-lg bg-amber-400/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">{label}</div>
                    <div className="text-white text-sm whitespace-pre-line">{value}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 rounded-xl bg-amber-500/10 border border-amber-400/20">
              <div className="text-amber-400 font-semibold text-sm mb-2">Response Time</div>
              <p className="font-suisse-intl text-gray-400 text-sm leading-relaxed">
                We typically respond within <span className="text-white font-medium">24 business hours</span>. For urgent inquiries, call us directly.
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            {submitted ? (
              <div className="h-full flex items-center justify-center p-12 rounded-2xl bg-white/5 border border-white/10 text-center">
                <div>
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <Heading as="h3" variant="cardTitleLight" className="mb-3">{successHeading}</Heading>
                  <p className="font-suisse-intl text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">
                    Thank you for reaching out. We have received your message and will be in touch within 24 business hours.
                  </p>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="p-8 rounded-2xl bg-white/5 border border-white/10 space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-gray-400 text-xs font-medium mb-2" htmlFor="name">
                      Full Name *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/60 focus:bg-white/15 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs font-medium mb-2" htmlFor="email">
                      Email Address *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/60 focus:bg-white/15 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-xs font-medium mb-2" htmlFor="company">
                    Company Name
                  </label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Your Company"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/60 focus:bg-white/15 transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-gray-400 text-xs font-medium mb-2" htmlFor="service">
                      Service Needed
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none focus:border-amber-400/60 focus:bg-white/15 transition-all appearance-none"
                    >
                      <option value="" className="bg-[#0a0f1e]">Select a service</option>
                      {services.map((s) => (
                        <option key={s} value={s} className="bg-[#0a0f1e]">{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs font-medium mb-2" htmlFor="budget">
                      Budget Range
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={form.budget}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none focus:border-amber-400/60 focus:bg-white/15 transition-all appearance-none"
                    >
                      <option value="" className="bg-[#0a0f1e]">Select budget</option>
                      {budgets.map((b) => (
                        <option key={b} value={b} className="bg-[#0a0f1e]">{b}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-xs font-medium mb-2" htmlFor="message">
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project, goals, and timeline..."
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/60 focus:bg-white/15 transition-all resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="group w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-xl transition-all duration-200 hover:shadow-2xl hover:shadow-amber-500/30 hover:-translate-y-0.5"
                >
                  Send Message
                  <Send className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
